import {h, Component} from 'preact/dist/preact'
import cx from 'classnames';
import {format} from 'date-fns';
import {isFunction} from '@util/ObjectUtils';
import {Link} from 'react-router-dom';
import Icon from '@components/common/Icon';
import {Address} from '@components/common/Address';
import Spinner from '@components/common/Spinner';
import TruncateText from '@components/common/TruncateText';
import {
    PageContent,
    ContactInfo,
    VendorLabel,
    TrackingLink,
    SmsPageError,
    InjuryBadge,
    RmaStatusBadge,
    RmaDateBadge,
    UnitStatus,
    EquipmentModelDetail,
    ShippingLabel
} from '../components';
import {box, space, labels} from '../styles';
import pluralize from 'pluralize';

function getRmaLineSerial ( rmaLineItems ) {
    return rmaLineItems.map( line => line.serialId ).join( ', ' );
}

export const RmaItem = ( {requestId, requestStatus, rmaLineItems, createdDate, route} ) =>
    <div data-testid={`rma-item-${requestId}`}
         className="w-full shadow-md rounded hover:shadow-1-strong min-w-xs">
        <div className="p-4">
            <h3 className="text-lg font-bold">
                Repair {requestId}
            </h3>
            <RmaDateBadge className="mt-2" date={createdDate}/>
            <div className="mt-4">
                <h4>
                    {rmaLineItems.length} {pluralize( 'item', rmaLineItems.length )}
                </h4>
                <TruncateText className="txt-c2" maxCharacters={48}>
                    {`Serial ${pluralize( 'number', rmaLineItems.length )} - ${getRmaLineSerial( rmaLineItems )}`}
                </TruncateText>
            </div>
            <div className="mt-4 md:mt-8 flex justify-between items-end">
                <RmaStatusBadge status={requestStatus}/>
                <Link to={`${route.match.url}/${requestId}`}
                      className="flex">
                    <span>Repair Details</span>
                    <Icon.ui name="arrow-right" className="text-lg ml-2"/>
                </Link>
            </div>
        </div>
    </div>;

export class RmaListContainer extends Component {

    state = {
        loading: false
    };

    componentDidMount () {
        // refetch RMA list anytime this view is loaded to keep it up-to-date
        this.setState( {loading: true} );
        this.props.rmaData.refetch()
            .then( () => this.setState( {loading: false} ) );
    }

    render ( props, context ) {
        const {rmaData, history, location, match} = props;

        if ( rmaData.error ) {
            return <SmsPageError/>
        }

        if ( this.state.loading ) {
            return <div className="mt-4 flex justify-center items-center">
                <Spinner className="md:text-base" message="Loading your repairs"/>
            </div>
        }

        let rmaList = rmaData.rmasByAccountNumber || [];

        return (
            <PageContent>
                {isFunction( this.props.children[0] ) && this.props.children[0]( {
                    rmasOpen: rmaList.filter( rma => rma.requestStatus === 'OPEN' ),
                    rmasCompleted: rmaList.filter( rma => /COMPLETE|CLOSED|CANCELED/.test( rma.requestStatus ) ),
                    history, location, match
                } )}
            </PageContent>
        );
    };
}

export const RmaList = ( {rmas, history, location, match} ) =>
    <div className="grid md:grid-col-2 lg:grid-col-3 grid-gap-8 items-start">
        {rmas.map( item =>
            <RmaItem key={item.requestId} {...item} route={{history, location, match}}/>
        )}
    </div>;

export const RmaListError = () =>
    <PageContent>
        <div className="mt-4">
            <div className="text-center mt-8 max-w-sm mx-auto">
                <h2 className="text-lg font-normal px-8">Sorry, there was a problem while trying to retrieve your equipment purchases</h2>
                <p className="mt-4">This could be due to a problem on our end or a poor internet connection. Please try refreshing this page.</p>
                <button className="button secondary mt-8" onClick={() => window.location.reload()}>Refresh page</button>
                <p className={`mt-16 ${box.info}`}>
                    If the problem continues please contact our customer service at
                    <span className="whitespace-no-wrap">{upi.conf.phoneTollFree}</span>.
                </p>
            </div>
        </div>
    </PageContent>;

export const RmaLineItem = props =>
    <div className="mt-8 flex w-full">
    <div className="flex-shrink self-center mr-8">
        <ProductImage images={props.images} kitName={props.itemName}/>
    </div>
    <div className="flex-1">
        <h4 className={labels.itemName}>{props.itemName}</h4>
        <div className="flex-1 md:mt-2 md:flex justify-between">
            <EquipmentModelDetail className="mt-2 md:mt-0" {...props} />
            <div className="mt-2 md:mt-0 md:w-80">
                <UnitStatus unitStatus={props.unitStatus}/>
            </div>
            <div className="flex-1 mt-2 md:mt-0 max-w-md md:ml-16">
                <h4 className="mt-4 md:mt-0">Warranty information</h4>
                <p>{props.yearsCoverage} years beginning {props.warrantyCoverageStart}</p>
                <h4 className="mt-2">Issue</h4>
                <p>{props.issue}</p>
            </div>
        </div>
    </div>
</div>;

const RmaLineStatus = ( {rmaStatus, requestLineStatus, unitStatus, outBoundTrackingNumber} ) => {
    if ( requestLineStatus === 'CANCELED' ) {
        return <p className="text-fail font-bold flex">Canceled</p>;
    }
    if ( requestLineStatus === 'CLOSED' ) {
        return <p className="font-bold">Closed</p>;
    }
    if ( outBoundTrackingNumber ) {
        return (
            <div>
                <p className="font-bold">Returned</p>
                <p className="mt-2 text-sm">Tracking number</p>
                <TrackingLink trackingNumber={outBoundTrackingNumber}/>
            </div>
        );
    }
    if ( rmaStatus === 'OPEN' ) {
        return <UnitStatus unitStatus={unitStatus}/>
    }

    return '--';
};

export const RmaLineItemShort = props => {

    const lineClassList = cx(
        'py-2 md:p-0 md:grid grid-col-5 items-center bg-white',
        box.divider,
        {
            'txt-c3': /CLOSED|CANCELED/.test( props.requestLineStatus )
        }
    );
    return (
        <div className={lineClassList}>
        <span className={`w-full block md:flex md:flex-col md:w-64 ${space.td}`}>
            <b className="block">{props.itemName}</b>
            <InjuryBadge hasInjury={props.injury}/>
        </span>
        <span className={`inline-block mt-2 mr-4 md:m-0 ${space.td}`}>
            <span className="text-sm txt-c2 md:hidden">Serial:</span> {props.serialId}
        </span>
        <span className={`mr-4 md:m-0 ${space.td}`}>
            <span className="text-sm txt-c2 md:hidden">LOT:</span> {props.lotId}
        </span>
        <span className={`mr-4 md:m-0 block ${space.td}`}>
            <span className="text-sm txt-c2 md:hidden">Date received: </span> {props.receivedDate
            ? format( props.receivedDate, 'MMM D, YYYY' )
            : <em className="txt-c3">not received</em>
        }
        </span>
        <div className={`flex items-center ${space.td}`}>
            <RmaLineStatus {...props} />
        </div>
    </div>
    );
};

export const RmaConfirmationItem = ( {upsLabel = {}, address = {}, vendorAddress, ...props} ) =>
    <div data-testid="rma-confirmation-item" className="mt-4 flex w-full p-4 rounded shadow-1">
        <div className="flex-1 relative">
            <h2 className="block text-lg">Shipment {props.rmaIndex[0] + 1} of {props.rmaIndex[1]}</h2>
            <h3 className="block text-lg font-normal">Repair Authorization</h3>
            <p>Repairs will be fulfilled by <VendorLabel name={props.vendor}/></p>
            <div className="md:mt-4 md:flex md:flex-row-reverse justify-between">
                <div className="mt-2 md:mt-0 md:ml-8">
                    <p className="md:text-lg">
                        Items must be returned within 30 days to be eligible for repair
                    </p>
                    <div className="mt-4">
                        <span className="block">Tracking Number:</span>
                        <TrackingLink trackingNumber={upsLabel.trackingNumber}/>
                    </div>
                    <div className="flex mt-4">
                        <ShippingLabel showLabel={true}
                                       image={upsLabel.image}
                                       trackingNumber={upsLabel.trackingNumber}/>
                    </div>
                </div>
                <div className="md:flex flex-wrap">
                    <div className="mt-2 md:mt-0 md:mr-16">
                        <h4>Issued by</h4>
                        <ContactInfo {...props}/>
                    </div>
                    <div className="mt-4 md:mt-0 md:mr-8">
                        <h4>Shipping to</h4>
                        <VendorLabel name={props.vendor}/>
                        <Address
                            address1={vendorAddress.address1}
                            address2={vendorAddress.address2}
                            city={vendorAddress.city}
                            state={vendorAddress.state}
                            postalCode={vendorAddress.postalCode}
                        />
                    </div>
                    <div className="mt-2 md:mt-0">
                        <h4>Shipping from</h4>
                        <Address
                            address1={address.address1}
                            address2={address.address2}
                            city={address.city}
                            state={address.state}
                            postalCode={address.postalCode}
                        />
                    </div>
                    {props.rmaLineItems.length > 0
                        ?
                        <div className="w-full">
                            <h4 className="mt-4 md:mt-8 font-normal md:text-lg">{props.rmaLineItems.length} item(s) in shipment</h4>
                            {props.rmaLineItems.map( line =>
                                <div className="mt-4 md:flex items-center max-w-md">
                                    <span className="block font-bold md:w-64">{line.itemName}</span>
                                    <EquipmentModelDetail serialId={line.serialId} lotId={line.lotId}/>
                                </div>
                            )}
                        </div>
                        :
                        <h4 className="mt-4 text-fail">No items in shipment</h4>
                    }
                </div>
            </div>

            <p className="mt-8 text-sm txt-c2 max-w-md">
                <b className="txt-c1">Important!</b> If the items we receive from you do not match the serial number(s) above,
            your repairs may not be performed and the item will be shipped back to you.
            </p>
        </div>
    </div>;